import { Link } from '@inertiajs/react'
import { ArrowLeft } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'
import { TopoContours } from '@/components/patterns'
import FundraisingProgressBar from '@/components/event-show/FundraisingProgressBar'
import ShareButtons from '@/components/ShareButtons'
import {
  CausesSection,
  DonationPanel,
  IndustriesSection,
  MobileDonateBar,
} from './shared'
import type { DetailOrganization, Event } from '@/types'

type Props = {
  event: Event
  organization: DetailOrganization | null
  brandFree: boolean
  eventUrl: string
  isPreview: boolean
}

export default function FundraiserTemplate({
  event,
  organization,
  brandFree,
  eventUrl,
  isPreview,
}: Props) {
  const hasImage = !!event.cover_image_url
  const showShare = event.status !== 'draft' && !brandFree && !isPreview

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8">
        {!brandFree && (
          <Link href="/" className="mb-4 inline-flex items-center gap-1.5 text-base text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-3.5" />
            Back to events
          </Link>
        )}
        {hasImage ? (
          <div className="relative h-72 w-full overflow-hidden rounded-2xl sm:h-96 lg:h-[28rem]">
            <img src={event.cover_image_url!} alt="" className="h-full w-full object-cover" />
          </div>
        ) : (
          <div className="relative h-72 w-full overflow-hidden rounded-2xl bg-linear-to-br from-[#F3722B] to-[#F7961E] sm:h-96 lg:h-[28rem]">
            <TopoContours />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(255,255,255,0.12),transparent)]" />
          </div>
        )}
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-4 sm:px-6 lg:px-8">
        <FundraisingProgressBar event={event} />
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-8 pb-24 sm:px-6 lg:px-8 lg:pb-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-2 [&>*+*]:border-t [&>*+*]:border-black/5 [&>*+*]:pt-5">
            <div>
              <h1 className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl">{event.title}</h1>
              {organization && (
                <p className="mt-2 inline-flex items-center gap-1.5 text-muted-foreground">
                  by{' '}
                  {brandFree ? (
                    <span className="font-medium text-foreground">{organization.name}</span>
                  ) : (
                    <Link href={`/o/${organization.slug}`} className="font-medium text-foreground underline-offset-4 hover:underline">
                      {organization.name}
                    </Link>
                  )}
                  {organization.verified && <VerifiedBadge className="size-4 text-foreground" />}
                </p>
              )}
              {showShare && (
                <div className="mt-4 max-w-xs">
                  <ShareButtons eventId={event.id} title={event.title} url={eventUrl} label="Share Fundraiser" />
                </div>
              )}
            </div>

            {event.description && (
              <div>
                <h2 className="mb-4 text-xl font-semibold">About this fundraiser</h2>
                <p className="max-w-[65ch] whitespace-pre-wrap text-pretty">{event.description}</p>
              </div>
            )}

            {organization && <CausesSection causes={organization.causes} />}
            {organization && <IndustriesSection industries={organization.industries} />}
          </div>

          <div className="lg:col-span-1">
            <div id="donate" className="sticky top-24 scroll-mt-24 rounded-2xl bg-muted/30 p-6 ring-1 ring-black/5">
              <DonationPanel event={event} organization={organization} isPreview={isPreview} />
            </div>
          </div>
        </div>
      </div>

      <MobileDonateBar event={event} organization={organization} isPreview={isPreview} />
    </>
  )
}
